import {Injectable, OnDestroy} from '@angular/core';
import {AuthService} from '@app/core/services/auth.service';
import {InternationalizationService} from '@app/core/services/internationalization.service';
import {HttpService} from '@app/core/services/http.service';
import {Observable, Subscription} from 'rxjs';
import {User} from '@app/core/models/user.model';
import {IUsersFilters, UserService as OldUserService} from '@app/modules/shared/services/user.service';
import {PaginationHolder} from '@app/core/models/pagination.model';
import {map} from 'rxjs/operators';
import {IEmployeeSalaryCalculation} from '@app/core/store/state/employees.state';
import {isFilled} from '@app/core/helpers';

@Injectable({
	providedIn: 'root',
})
export class UserService implements OnDestroy {
	public subscriptions: Subscription[] = [];

	constructor(
		public authService: AuthService,
		public interService: InternationalizationService,
		public httpService: HttpService,
		private oldUserService: OldUserService,
	) {
	}

	public getUsers(filters: IUsersFilters = {}, page_size: number = 200, page: number = 1, include_deactivated: boolean = false): Observable<User[]> {
		if (!isFilled(filters['filter[departments.id]'])) {
			delete filters['filter[departments.id]'];
		}
		const data = {
			...filters,
			page_size,
			page,
		};
		if (include_deactivated) {
			data['include_deactivated'] = true;
		}

		data['include'] = 'groups, departments';

		return this.httpService.getJson<PaginationHolder<User>>('users', data)
			.pipe(map(res => res.data.map(u => this.oldUserService.fresh(u))));
	}

	public getUser(id: number): Observable<User> {
		return this.httpService.getJson<User>('users/' + String(id))
			.pipe(map(u => {
				// map departments from the available_departments (so that they are the same)
				const departmentIds = u.departments.map(department => department.id);
				u.departments = u.available_departments.filter(department => departmentIds.indexOf(department.id) !== -1);

				return this.oldUserService.fresh(u);
			}));
	}

	public postUser(payload: {}): Observable<{ success: boolean, error: {} }> {
		return this.httpService.postJson<{ success: boolean, error: {} }>('users', payload);
	}

	public patchUser(userId: number, payload: {}): Observable<{ success: boolean, error: {} }> {
		return this.httpService.patchJson<{ success: boolean, error: {} }>('users/' + userId, payload);
	}

	public deleteUser(id: number): Observable<{ success: boolean, error: {} }> {
		return this.httpService.deleteJson<{ success: boolean, error: {} }>('users/' + String(id));
	}

	public patchProfile(payload: {}): Observable<{ success: boolean, error: {} }> {
		return this.httpService.patchJson<{ success: boolean, error: {} }>('users/details', payload);
	}

	public sendActivationMail(user: User): Observable<{ success: boolean, error: {} }> {
		return this.httpService.postJson<{ success: boolean, error: {} }>('users/' + user.id + '/reSendEmail', {});
	}

	public getSocialSecurityNumber(userId: number): Observable<{ social_security_number: string }> {
		return this.httpService.getJson<{ social_security_number: string }>('users/' + userId + '/socialSecurityNumber');
	}

	public getEmployeeSalaryTotalsCalculation(userId: number, year: number, month: number, includeShifts: boolean): Observable<IEmployeeSalaryCalculation> {
		return this.httpService.getJson<IEmployeeSalaryCalculation>('salary/users/' + userId, {
			year,
			month,
			include_shifts: includeShifts
		});
	}

	public ngOnDestroy(): void {
		this.subscriptions.forEach(s => s.unsubscribe());
	}
}
